import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Severity } from 'Contexts/RestaurantContext';
import { db } from 'utils/firebase';
import { asyncVoid, noop } from 'utils/helper';
import logFirestoreError from 'utils/logFirestoreError';
import server from 'utils/server';
import useToast from './useToast';
import { triggerCustomEvent } from './useCustomEvent';

export type TablePlanAssistantTableChange = {
  type: 'tableChanged';
  reservationId: string;
  reservationName?: string;
  before: { tables: string[]; space: string };
  after: { tables: string[]; space: string };
};

export type TablePlanAssistantBlockChange = {
  type: 'block';
  tables: string[];
};

export type TablePlanAssistantUnblockChange = {
  type: 'unblock';
  tables: string[];
};

export type TablePlanAssistantCloseSpaceChange = {
  type: 'closeSpace';
  space: string;
};

export type TablePlanAssistantOpenSpaceChange = {
  type: 'openSpace';
  space: string;
};

export type TablePlanAssistantChange =
  | TablePlanAssistantTableChange
  | TablePlanAssistantBlockChange
  | TablePlanAssistantUnblockChange
  | TablePlanAssistantCloseSpaceChange
  | TablePlanAssistantOpenSpaceChange;

export type TablePlanAssistantPendingInteractionOption = {
  id: string;
  label: string;
  value?: string;
  description?: string;
};

export type TablePlanAssistantPendingInteraction = {
  id: string;
  type: 'permission' | 'multipleChoice';
  title?: string;
  question: string;
  options: TablePlanAssistantPendingInteractionOption[];
  createdAt: number;
};

export type TablePlanAssistantFeedbackRating = 'up' | 'down';

export type TablePlanAssistantMessageFeedback = {
  rating: TablePlanAssistantFeedbackRating;
  ratedAt: number;
  ratedBy?: string;
  changeCount?: number;
  actionsSnapshot?: TablePlanAssistantChange[];
  sessionTitle?: string;
};

export type TablePlanAssistantSession = {
  id: string;
  title?: string;
  date: string;
  restaurantId: string;
  applied?: boolean;
  appliedAt?: number;
  appliedBy?: string;
  changes: TablePlanAssistantChange[];
  summary: string;
  pendingInteraction?: TablePlanAssistantPendingInteraction | null;
  userId: string;
  createdAt: number;
  updatedAt?: number;
};

export type TablePlanAssistantMessage = {
  id?: string;
  sessionId: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: number;
  feedback?: TablePlanAssistantMessageFeedback | null;
};

export type TablePlanAssistantSaveViolation = {
  code: string;
  message: string;
  reservationId?: string;
};

type TablePlanAssistantReservationStateIds = {
  startedReservationIds: string[];
  doneReservationIds: string[];
  blockedReservationIds: string[];
};

type StreamEvent = {
  type: string;
  message?: string;
  title?: string;
  sessionId?: string;
  functionName?: string;
  result?: any;
  changes?: TablePlanAssistantChange[];
  pendingInteraction?: TablePlanAssistantPendingInteraction | null;
  awaitingUser?: boolean;
  done?: boolean;
};

export type TablePlanAssistantHook = {
  active: boolean;
  sessionId: string | null;
  session: TablePlanAssistantSession | null;
  messages: TablePlanAssistantMessage[];
  liveMessage: string;
  changes: TablePlanAssistantChange[];
  streaming: boolean;
  saving: boolean;
  loadingSession: boolean;
  error: string | null;
  saveRequiresConfirmation: boolean;
  saveViolations: TablePlanAssistantSaveViolation[];
  pendingInteraction: TablePlanAssistantPendingInteraction | null;
  openSession: (sessionId: string) => void;
  close: () => void;
  createSession: (content: string) => Promise<void>;
  updateSession: (content: string) => Promise<void>;
  respondToPendingInteraction: (
    option: TablePlanAssistantPendingInteractionOption,
  ) => Promise<void>;
  dismissSaveConfirmation: () => void;
  saveSession: (confirmRuleViolations?: boolean) => Promise<boolean>;
  rateMessage: (
    messageId: string,
    rating: 'up' | 'down' | null,
  ) => Promise<void>;
  undoChange: (index: number) => Promise<void>;
  clearChanges: () => Promise<void>;
};

export const TABLE_PLAN_ASSISTANT_DEFAULT: TablePlanAssistantHook = {
  active: false,
  sessionId: null,
  session: null,
  messages: [],
  liveMessage: '',
  changes: [],
  streaming: false,
  saving: false,
  loadingSession: false,
  error: null,
  saveRequiresConfirmation: false,
  saveViolations: [],
  pendingInteraction: null,
  openSession: noop,
  close: noop,
  createSession: asyncVoid,
  updateSession: asyncVoid,
  respondToPendingInteraction: asyncVoid,
  dismissSaveConfirmation: noop,
  saveSession: async () => false,
  rateMessage: asyncVoid,
  undoChange: asyncVoid,
  clearChanges: asyncVoid,
};

const ASSISTANT_MESSAGES_COLLECTION = 'tablePlanAssistantSessionMessages';

const getStreamUrl = (path: string) => {
  const baseUrl = server.defaults.baseURL || '';
  return `${baseUrl}${path}`;
};

const parseErrorMessage = async (response: Response) => {
  try {
    const text = await response.text();
    const json = JSON.parse(text);

    return json?.error || json?.message || text;
  } catch (error) {
    return (
      response.statusText || 'Der Assistent konnte nicht gestartet werden.'
    );
  }
};

const readStream = async (
  response: Response,
  onEvent: (event: StreamEvent) => void,
) => {
  if (!response.body) return;

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();

    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const events = buffer.split('\n\n');
    buffer = events.pop() ?? '';

    events.forEach((eventBlock) => {
      const dataLines = eventBlock
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.replace(/^data:\s?/, ''));

      if (!dataLines.length) return;

      try {
        onEvent(JSON.parse(dataLines.join('\n')) as StreamEvent);
      } catch (error) {
        console.error('tablePlanAssistant stream parse error', error);
      }
    });
  }
};

const useTablePlanAssistant = ({
  restaurantId,
  date,
  uid,
  reservationStateIds,
}: {
  restaurantId: string | null;
  date: string;
  uid: string | null;
  reservationStateIds?: TablePlanAssistantReservationStateIds;
}): TablePlanAssistantHook => {
  const toast = useToast();
  const abortController = useRef<AbortController | null>(null);

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [session, setSession] = useState<TablePlanAssistantSession | null>(
    null,
  );
  const [messages, setMessages] = useState<TablePlanAssistantMessage[]>([]);
  const [pendingUserMessage, setPendingUserMessage] =
    useState<TablePlanAssistantMessage | null>(null);
  const [localChanges, setLocalChanges] = useState<TablePlanAssistantChange[]>(
    [],
  );
  const [draftChanges, setDraftChanges] = useState<
    TablePlanAssistantChange[] | null
  >(null);
  const [liveMessage, setLiveMessage] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveRequiresConfirmation, setSaveRequiresConfirmation] =
    useState(false);
  const [saveViolations, setSaveViolations] = useState<
    TablePlanAssistantSaveViolation[]
  >([]);
  const [localPendingInteraction, setLocalPendingInteraction] =
    useState<TablePlanAssistantPendingInteraction | null>(null);

  const changes = useMemo(
    () => draftChanges ?? session?.changes ?? localChanges ?? [],
    [draftChanges, session?.changes, localChanges],
  );

  const displayMessages = useMemo(() => {
    if (!pendingUserMessage) return messages;

    const pendingIsSynced = messages.some(
      (message) =>
        message.role === 'user' &&
        message.content === pendingUserMessage.content,
    );

    return pendingIsSynced ? messages : [...messages, pendingUserMessage];
  }, [messages, pendingUserMessage]);

  const pendingInteraction = useMemo(
    () => localPendingInteraction ?? session?.pendingInteraction ?? null,
    [localPendingInteraction, session?.pendingInteraction],
  );

  useEffect(() => {
    if (!restaurantId) {
      setSessionId(null);
    }
  }, [restaurantId]);

  useEffect(() => {
    setDraftChanges(null);

    if (!sessionId) {
      setSession(null);
      setMessages([]);
      setLocalChanges([]);
      setLiveMessage('');
      setLocalPendingInteraction(null);
      setLoadingSession(false);
      return;
    }

    setLoadingSession(true);

    const sessionRef = db
      .collection('tablePlanAssistantSessions')
      .doc(sessionId);
    const unsubscribeSession = sessionRef.onSnapshot(
      (doc) => {
        setSession(
          doc.exists
            ? ({
                ...doc.data(),
                id: doc.id,
                changes: doc.data()?.changes ?? [],
                summary: doc.data()?.summary ?? '',
                title: doc.data()?.title ?? '',
                pendingInteraction: doc.data()?.pendingInteraction ?? null,
              } as TablePlanAssistantSession)
            : null,
        );
        setLoadingSession(false);
      },
      (err) => {
        logFirestoreError(
          'useTablePlanAssistant',
          `tablePlanAssistantSessions/${sessionId}`,
          err,
        );
        setError(err.message);
        setLoadingSession(false);
      },
    );

    const unsubscribeMessages = sessionRef
      .collection(ASSISTANT_MESSAGES_COLLECTION)
      .orderBy('createdAt', 'asc')
      .onSnapshot(
        (snapshot) => {
          const nextMessages = snapshot.docs.map(
            (doc) =>
              ({
                ...doc.data(),
                id: doc.id,
              } as TablePlanAssistantMessage),
          );

          setMessages(nextMessages);
          setPendingUserMessage((pendingMessage) => {
            if (!pendingMessage) return pendingMessage;

            const pendingIsSynced = nextMessages.some(
              (message) =>
                message.role === 'user' &&
                message.content === pendingMessage.content,
            );

            return pendingIsSynced ? null : pendingMessage;
          });
        },
        (err) => {
          logFirestoreError(
            'useTablePlanAssistant',
            `tablePlanAssistantSessions/${sessionId}/${ASSISTANT_MESSAGES_COLLECTION}`,
            err,
          );
          setError(err.message);
        },
      );

    return () => {
      unsubscribeSession();
      unsubscribeMessages();
    };
  }, [sessionId]);

  const handleStreamEvent = useCallback(
    (event: StreamEvent) => {
      const applyTitle = (nextTitle: string, nextSessionId?: string) => {
        setSession((currentSession) =>
          currentSession
            ? { ...currentSession, title: nextTitle }
            : nextSessionId
            ? ({
                id: nextSessionId,
                title: nextTitle,
                date,
                restaurantId: restaurantId ?? '',
                changes: [],
                summary: '',
                userId: uid ?? '',
                createdAt: Date.now(),
              } as TablePlanAssistantSession)
            : currentSession,
        );
      };

      if (event.type === 'session' && event.sessionId) {
        setSessionId(event.sessionId);
        if (event.title) {
          applyTitle(event.title, event.sessionId);
        }
      }

      if (event.type === 'session_title' && event.title) {
        applyTitle(event.title, event.sessionId);
      }

      if (event.type === 'message' && event.message) {
        setLiveMessage((message) =>
          [message, event.message].filter(Boolean).join('\n\n'),
        );
      }

      if (event.type === 'tool_result') {
        const nextChanges =
          event.changes ?? event.result?.changes ?? event.result?.recentChanges;

        if (Array.isArray(nextChanges)) {
          setDraftChanges(null);
          setLocalChanges(nextChanges);
        }
      }

      if (event.type === 'pending_interaction' && event.pendingInteraction) {
        setLocalPendingInteraction(event.pendingInteraction);
      }

      if (event.type === 'error') {
        setError(event.message ?? 'Der Assistent hat einen Fehler gemeldet.');
      }

      if (event.type === 'done') {
        setStreaming(false);
      }
    },
    [date, restaurantId, uid],
  );

  const sendStreamRequest = useCallback(
    async (path: string, content: string) => {
      if (!restaurantId || !date || !uid) {
        setError('Restaurant, Datum oder Nutzer fehlt.');
        return;
      }

      abortController.current?.abort();
      abortController.current = new AbortController();

      setError(null);
      setSaveRequiresConfirmation(false);
      setSaveViolations([]);
      setLiveMessage('');
      setLocalPendingInteraction(null);
      setPendingUserMessage({
        sessionId: sessionId ?? 'pending',
        role: 'user',
        content,
        createdAt: Date.now(),
      });
      setStreaming(true);

      try {
        const response = await fetch(getStreamUrl(path), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Custom-Header': 'dashboard',
            uid,
          },
          body: JSON.stringify({
            restaurantId,
            date,
            uid,
            content,
            ...(reservationStateIds ?? {}),
          }),
          signal: abortController.current.signal,
        });

        if (!response.ok) {
          throw new Error(await parseErrorMessage(response));
        }

        await readStream(response, handleStreamEvent);
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          setError(
            err?.message ?? 'Der Assistent konnte nicht erreicht werden.',
          );
        }
      } finally {
        setStreaming(false);
      }
    },
    [
      date,
      handleStreamEvent,
      reservationStateIds,
      restaurantId,
      sessionId,
      uid,
    ],
  );

  const close = useCallback(() => {
    abortController.current?.abort();
    setSessionId(null);
    setSession(null);
    setMessages([]);
    setPendingUserMessage(null);
    setLocalChanges([]);
    setDraftChanges(null);
    setLiveMessage('');
    setLocalPendingInteraction(null);
    setError(null);
    setSaveRequiresConfirmation(false);
    setSaveViolations([]);
    setStreaming(false);
    setSaving(false);
  }, []);

  const createSession = useCallback(
    async (content: string) => {
      await sendStreamRequest(
        '/v03/reservations/tablePlanAssistant/create',
        content,
      );
    },
    [sendStreamRequest],
  );

  const updateSession = useCallback(
    async (content: string) => {
      if (!sessionId) {
        await createSession(content);
        return;
      }

      if (session?.applied) {
        toast(
          'Diese Sitzung wurde bereits gespeichert.',
          Severity.INFO,
          'reservations',
        );
        close();
        return;
      }

      await sendStreamRequest(
        `/v03/reservations/tablePlanAssistant/${sessionId}/update`,
        content,
      );
    },
    [
      close,
      createSession,
      sendStreamRequest,
      session?.applied,
      sessionId,
      toast,
    ],
  );

  const respondToPendingInteraction = useCallback(
    async (option: TablePlanAssistantPendingInteractionOption) => {
      if (!pendingInteraction) return;

      // The question is now persisted as an assistant message (backend), so the
      // user's reply can just be the plain human label — no "Antwort auf
      // Rückfrage …" wrapper, and prefer the readable label over a machine value.
      const content = option.label || option.value || '';

      setLocalPendingInteraction(null);
      await updateSession(content);
    },
    [pendingInteraction, updateSession],
  );

  const dismissSaveConfirmation = useCallback(() => {
    setSaveRequiresConfirmation(false);
    setSaveViolations([]);
    setError(null);
  }, []);

  const updateDraftChanges = useCallback(
    async (nextChanges: TablePlanAssistantChange[]) => {
      if (saving) return;

      if (session?.applied) {
        setError('Diese Sitzung wurde bereits gespeichert.');
        return;
      }

      setDraftChanges(nextChanges);
      setSaveRequiresConfirmation(false);
      setSaveViolations([]);

      if (!sessionId) {
        setLocalChanges(nextChanges);
        return;
      }

      try {
        await db
          .collection('tablePlanAssistantSessions')
          .doc(sessionId)
          .update({
            changes: nextChanges,
            updatedAt: Date.now(),
          });
      } catch (err: any) {
        setError(
          err?.message ?? 'Die Änderung konnte nicht zurückgenommen werden.',
        );
      }
    },
    [saving, session?.applied, sessionId],
  );

  const undoChange = useCallback(
    async (index: number) => {
      await updateDraftChanges(
        changes.filter((_, changeIndex) => changeIndex !== index),
      );
    },
    [changes, updateDraftChanges],
  );

  const clearChanges = useCallback(async () => {
    await updateDraftChanges([]);
  }, [updateDraftChanges]);

  const openSession = useCallback((nextSessionId: string) => {
    setError(null);
    setSaveRequiresConfirmation(false);
    setSaveViolations([]);
    setLiveMessage('');
    setPendingUserMessage(null);
    setLocalPendingInteraction(null);
    setDraftChanges(null);
    setSessionId(nextSessionId);
  }, []);

  const saveSession = useCallback(
    async (confirmRuleViolations = false) => {
      if (!sessionId || !restaurantId || !date || !uid || saving) return false;

      if (session?.applied) {
        toast(
          'Diese Sitzung wurde bereits gespeichert.',
          Severity.INFO,
          'reservations',
        );
        close();
        return true;
      }

      setSaving(true);
      setError(null);
      if (!confirmRuleViolations) {
        setSaveRequiresConfirmation(false);
        setSaveViolations([]);
      }

      try {
        await server.post(
          `/v03/reservations/tablePlanAssistant/${sessionId}/save`,
          {
            restaurantId,
            date,
            uid,
            changes,
            confirmRuleViolations,
          },
        );

        triggerCustomEvent('triggerShiftReload');
        toast(
          'Änderungen wurden gespeichert.',
          Severity.SUCCESS,
          'reservations',
        );
        setSaveRequiresConfirmation(false);
        setSaveViolations([]);
        close();
        return true;
      } catch (err: any) {
        const requiresConfirmation =
          !!err?.response?.data?.requiresConfirmation;
        const violations = Array.isArray(err?.response?.data?.violations)
          ? err.response.data.violations
          : [];
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Die Änderungen konnten nicht gespeichert werden.';

        if (err?.response?.status === 409 && !requiresConfirmation) {
          toast(message, Severity.INFO, 'reservations');
          close();
          return true;
        }

        setSaveRequiresConfirmation(requiresConfirmation);
        setSaveViolations(violations);
        setError(message);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [
      changes,
      close,
      date,
      restaurantId,
      saving,
      session?.applied,
      sessionId,
      toast,
      uid,
    ],
  );

  const rateMessage = useCallback(
    async (messageId: string, rating: 'up' | 'down' | null) => {
      if (!sessionId || !restaurantId || !date || !uid) return;

      try {
        await server.post(
          `/v03/reservations/tablePlanAssistant/${sessionId}/messages/${messageId}/rate`,
          {
            restaurantId,
            date,
            uid,
            rating,
            changes,
          },
        );
      } catch (err: any) {
        toast(
          err?.response?.data?.message ||
            err?.message ||
            'Bewertung konnte nicht gespeichert werden.',
          Severity.WARNING,
          'reservations',
        );
      }
    },
    [changes, date, restaurantId, sessionId, toast, uid],
  );

  return {
    active: !!sessionId || streaming || !!pendingUserMessage || !!error,
    sessionId,
    session,
    messages: displayMessages,
    liveMessage,
    changes,
    streaming,
    saving,
    loadingSession,
    error,
    saveRequiresConfirmation,
    saveViolations,
    pendingInteraction,
    openSession,
    close,
    createSession,
    updateSession,
    respondToPendingInteraction,
    dismissSaveConfirmation,
    saveSession,
    rateMessage,
    undoChange,
    clearChanges,
  };
};

export default useTablePlanAssistant;
